import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, googleAuthProvider } from '../../../firebaseConfig';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  onAuthStateChanged,
} from 'firebase/auth';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate('/admin/staff');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleError = (error) => {
    console.error('Error Code:', error.code, 'Error Message:', error.message);
    switch (error.code) {
      case 'auth/user-not-found':
        return 'No user found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      case 'auth/invalid-credential':
        return 'Invalid credentials. Ensure the login method matches.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoggingIn(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/admin/staff');
    } catch (error) {
      setError(handleError(error));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setIsLoggingIn(true);
    try {
      await signInWithPopup(auth, googleAuthProvider);
      navigate('/admin/staff');
    } catch (error) {
      setError(handleError(error));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setError('Password reset email sent! Check your Spam folder!');
    } catch (error) {
      setError(handleError(error));
    }
  };

  return (
    <div className='d-flex align-items-center justify-content-center min-vh-100 bg-light px-3'>
      <div className='w-100' style={{ maxWidth: '400px' }}>
        <div className='card shadow border-0 mt-4'>
          <div className='card-body p-4'>
            <h2 className='text-center text-primary mb-2'>Admin Access</h2>
            <p className='text-center text-muted mb-3'>
              Authorized personnel only. Log in with credentials or Google.
            </p>

            {error && (
              <div className='alert alert-danger text-center py-2' role='alert'>
                {error}
              </div>
            )}

            <form onSubmit={handleEmailLogin}>
              <div className='mb-3'>
                <label
                  htmlFor='email-address'
                  className='form-label fw-semibold'
                >
                  Email Address
                </label>
                <input
                  id='email-address'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  className='form-control'
                  placeholder='admin@example.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className='mb-3'>
                <label htmlFor='password' className='form-label fw-semibold'>
                  Password
                </label>
                <input
                  id='password'
                  name='password'
                  type='password'
                  required
                  className='form-control'
                  placeholder='••••••••'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <button
                type='submit'
                disabled={isLoggingIn}
                className={`btn btn-primary w-100 ${
                  isLoggingIn ? 'disabled' : ''
                }`}
              >
                {isLoggingIn ? 'Logging in...' : 'Login with Email'}
              </button>
            </form>

            <div className='my-3 text-center position-relative'>
              <hr />
              <span className='position-absolute top-50 start-50 translate-middle bg-white px-2 text-muted'>
                or
              </span>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={isLoggingIn}
              className={`btn btn-danger w-100 ${
                isLoggingIn ? 'disabled' : ''
              }`}
            >
              {isLoggingIn ? 'Logging in...' : 'Login with Google'}
            </button>

            <div className='text-center mt-3'>
              <button
                onClick={handleForgotPassword}
                className='btn btn-link text-decoration-none text-primary'
              >
                Forgot Password?
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
